import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, Inject, NgZone, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    BaseComponent,
    getTranslationLanguageKey,
    getTwoLetterLanguageCode,
    HeaderConfigService,
    HeaderEventsDirective,
    TrackingService,
} from '@os-components';
import {
    SewWsFloatingButtonsContactOptions,
    SewWsFooterApi,
    SewWsHeader,
} from '@sew/ui-components-angular/standalone';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { FsService } from './components/fs/fs.service';
import { ScrollService } from './features/landing-page/services/scroll.service';
import { tracking } from './helpers/tracking';
import { BlogService } from './services/blog/blog.service';
import { RouterService } from './shared/services/router.service';

@Component({
    selector: 'blog-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterOutlet,
        CommonModule,
        InfiniteScrollDirective,
        SewWsHeader,
        SewWsFloatingButtonsContactOptions,
        SewWsFooterApi,
        HeaderEventsDirective,
    ],
})
export class AppComponent extends BaseComponent implements OnInit {
    public environment = environment;
    public useCMP = environment.useCMP;
    public title = 'Blog | SEW-EURODRIVE';
    public currentCountry?: string;
    public currentLanguage?: string;

    public authConfig = environment.auth;
    public stage = environment.stage;

    public constructor(
        public sewConfigService: HeaderConfigService,
        public translateService: TranslateService,
        private scrollService: ScrollService,
        private fsService: FsService,
        private blogService: BlogService,
        private routerService: RouterService,
        private ngZone: NgZone /*         private router: Router,*/,
        private trackingService: TrackingService,
        @Inject(DOCUMENT) private document: Document
    ) {
        super(sewConfigService, translateService, environment);
    }

    public ngOnInit(): void {
        this.getContextFromQueryParams();

        // set global js variable for etracker
        (window as any).et_areas = 'Blog';

        this.trackingService.trackEvent(tracking.step.init);

        if (window.location.hostname.includes('localhost')) {
            this.baseURL = 'https://www.sew-d.de';
        } else {
            this.baseURL = window.location.protocol + '//' + window.location.hostname;
        }
        this.caasBaseUrl = environment.caas.baseUrl;
        this.apiBaseUrl = environment.baseURLs.api.international;

        this.translateService.onLangChange.subscribe(() => {
            this.setFooterLinks();
        });

        this.languageObservable.subscribe((lang: string) => {
            this.setLanguage(lang);
        });

        const countryShortChange = this.sewConfigService.countryShort;

        combineLatest([this.languageObservable, countryShortChange])
            .pipe(filter(([language, countryShort]) => language.length > 0 && countryShort.length > 0))
            .subscribe(() => {
                this.blogService.loadLabels();
            });

        if (this.environment.preview) {
            this.initOCM();
            const parentQueryString = parent.location.search;
            const parentUrlParams = new URLSearchParams(parentQueryString);
            const element = parentUrlParams.get('element');
            const blogQueryString = location.search;
            const blogUrlParams = new URLSearchParams(blogQueryString);
            const showHomepage = blogUrlParams.get('showHomepage');
            if (element && !showHomepage) {
                this.blogService.getPostByNodeId(element).subscribe((response) => {
                    if (response) {
                        this.routerService.navigateArticle(response);
                    } else {
                        this.blogService.getAuthorByNodeId(element).subscribe((author) => {
                            if (author) {
                                this.routerService.navigateAuthor(author);
                            }
                        });
                    }
                });
            }
        }
    }

    public onScroll(): void {
        this.scrollService.onScroll();
    }

    public navigateLandingPage() {
        this.ngZone.run(() => {
            this.routerService.navigateLandingPage();
        });
    }

    private setLanguage(language: string) {
        const languageKey = getTranslationLanguageKey(language);
        this.language = language.toUpperCase();
        this.translateService.use(languageKey);
        this.document.documentElement.lang = getTwoLetterLanguageCode(languageKey);
    }

    private initOCM(): void {
        const head = document.getElementsByTagName('head')[0] || document.documentElement;
        const script1 = document.createElement('script');
        script1.src = `https://${this.environment.cms}/fs5webedit/live/api.jsp`;
        script1.setAttribute('data-firstspirit-origin', `https://${this.environment.cms}/blog/`);
        script1.onload = () => this.fsService.initOCM();
        head.appendChild(script1);

        this.fsService.requestPreviewElement.subscribe((previewId) => {
            this.blogService.getPostByPreviewId(previewId).subscribe((response) => {
                if (response) {
                    this.ngZone.run(() => {
                        this.routerService.navigateArticle(response);
                    });
                } else {
                    this.blogService.getAuthorByPreviewId(previewId).subscribe((author) => {
                        if (author) {
                            this.ngZone.run(() => {
                                this.routerService.navigateAuthor(author);
                            });
                        }
                    });
                }
            });
        });
    }

    private getContextFromQueryParams() {
        const queryParams = new URLSearchParams(window.location.search);
        const countryParam: string | null = queryParams.get('country');
        if (countryParam) {
            this.currentCountry = countryParam.toLowerCase();
            this.fsService.previewCountry = countryParam;
        }
        const languageParam: string | null = queryParams.get('language');
        if (languageParam) {
            this.currentLanguage = languageParam;
            this.fsService.previewLanguage = this.currentLanguage;
        } else {
            // fallback to parent language param. This is probably useful in the content creator
            const parentLanguageParam = new URLSearchParams(window.parent.location.search).get('language');
            if (parentLanguageParam) {
                this.currentLanguage = parentLanguageParam;
                this.fsService.previewLanguage = this.currentLanguage;
            }
        }
    }
}
